import ErrorDetails from "../types/ErrorDetails";

const spUnableToStartLoginError: ErrorDetails = {
  errorCode: 500,
  errorMessage:
    "We were unable to securely start your login process. This may be a temporary connection issue. Please refresh the page and try again.",
};

const spUnableToFindDestinationPageError: ErrorDetails = {
  errorCode: 404,
  errorMessage:
    "You have successfully logged in, but we couldn't find your destination page. Please return to the homepage to continue.",
};

const spUnableToFindAccountError: ErrorDetails = {
  errorCode: 500,
  errorMessage:
    "Your login was successful, but we encountered an error getting your account details. Please try again in a few minutes.",
};

const spSessionError: ErrorDetails = {
  errorCode: 500,
  errorMessage:
    "We successfully verified your account, but we encountered an error setting up your secure session. Please try logging in again.",
};

const idpConfigMismatchError: ErrorDetails = {
  errorCode: 401,
  errorMessage: "Your login provider is sending data that doesn't match our settings. Please contact OSTTRA Support.",
};

const lsSessionSvcError: ErrorDetails = {
  errorCode: 500,
  errorMessage:
    "We are experiencing technical difficulties connecting to our session service. Please wait a moment and try again.",
};

const asSessionDataMissingError: ErrorDetails = {
  errorCode: 401,
  errorMessage: "Your secure sign-in data is no longer available. Please restart your sign-in from the application.",
};

const asInvalidClientError: ErrorDetails = {
  errorCode: 401,
  errorMessage: "This application is not registered in our system. Access is denied.",
};

const ERROR_MAP: Record<string, ErrorDetails> = {
  sp_invalid_redirect_url: {
    errorCode: 400,
    errorMessage:
      "The application you are trying to access is sending an unrecognized return address. For security reasons, we cannot complete your login.",
  },
  sp_reg_id_not_found: {
    errorCode: 404,
    errorMessage:
      "We couldn't find a Single Sign-On configuration for your email domain. Please verify your email address or contact OSTTRA Support Team to enable SSO for your organization.",
  },
  sp_reg_id_error: {
    errorCode: 500,
    errorMessage:
      "We encountered a technical issue while identifying your organization. Please try again later or contact the OSTTRA Support Team if the problem persists.",
  },
  sp_validation_failed: {
    errorCode: 401,
    errorMessage: "Login was successful, but your account could not be verified by our system.",
  },
  sp_relay_state_missing: {
    errorCode: 400,
    errorMessage:
      "We couldn't verify your original login request. Please go back to the application and try logging in again.",
  },
  sp_saml_payload_missing: {
    errorCode: 400,
    errorMessage:
      "We received a response from your login provider, but it was missing the required security data. Please try logging in again.",
  },
  sp_correlation_id_missing: {
    errorCode: 401,
    errorMessage:
      "We couldn't verify the link between your login request and the provider's response. Please restart your login for security reasons.",
  },
  sp_nonce_retrieval_error: {
    errorCode: 500,
    errorMessage:
      "We are temporarily unable to verify your login session. This is likely a connection issue; please try again in a few minutes.",
  },
  sp_nonce_expired: {
    errorCode: 401,
    errorMessage:
      "Your login session has timed out for security reasons, or this link has already been used. Please restart your login from the beginning.",
  },
  sp_saml_format_invalid: {
    errorCode: 400,
    errorMessage:
      "Your login provider sent a response in an unrecognized format. Please try again or contact your administrator.",
  },
  sp_idp_identity_missing: {
    errorCode: 401,
    errorMessage: "The login response is missing required identification. Please contact your IT administrator.",
  },
  sp_idp_not_registered: {
    errorCode: 404,
    errorMessage:
      "We don't recognize your organization's login provider. Please ensure you are using the correct login link.",
  },
  sp_internal_server_error: {
    errorCode: 500,
    errorMessage:
      "We encountered an unexpected problem while processing your login. Please try again in a few minutes or contact OSTTRA support if the issue continues.",
  },
  sp_auth_principal_invalid: {
    errorCode: 401,
    errorMessage:
      "We successfully verified your login, but we couldn't establish your user profile. Please try again or contact support.",
  },
  sp_app_access_denied: {
    errorCode: 403,
    errorMessage:
      "You do not have permission to access this specific application. Please contact OSTTRA Support Team to request access.",
  },
  sp_auth_identity_mismatch: {
    errorCode: 403,
    errorMessage:
      "The account used to sign in does not match the initial request. Please ensure you are logged into the correct provider account and try again.",
  },
  sp_auth_success_handler_failed: {
    errorCode: 500,
    errorMessage:
      "Your identity was verified, but an unexpected error occurred while finishing your login. Please try again or contact support.",
  },
  sp_auth_failed: {
    errorCode: 401,
    errorMessage: "We couldn't complete your login. Please try again or contact OSTTRA Support.",
  },
  sp_saml_assertion_expired: {
    errorCode: 401,
    errorMessage: "The security token received is no longer valid. Please restart your login process.",
  },
  sp_token_lookup_failed: {
    errorCode: 500,
    errorMessage:
      "We verified your identity, but we couldn't issue an access token. Please try logging in again or contact support.",
  },
  sp_target_state_persistence_error: spUnableToStartLoginError,
  sp_auth_req_id_missing: spUnableToStartLoginError,
  sp_nonce_persistence_error: spUnableToStartLoginError,
  sp_initiation_failed: spUnableToStartLoginError,
  sp_relay_state_lost_on_success: spUnableToFindDestinationPageError,
  sp_relay_state_not_found: spUnableToFindDestinationPageError,
  sp_application_lookup_failed: spUnableToFindAccountError,
  sp_user_details_lookup_failed: spUnableToFindAccountError,
  sp_session_serialization_failed: spSessionError,
  sp_session_creation_failed: spSessionError,
  sp_saml_signature_invalid: idpConfigMismatchError,
  sp_idp_config_mismatch: idpConfigMismatchError,
  ls_ip_blocked: {
    errorCode: 403,
    errorMessage: "Access from your current IP address is not authorized for your organization. Please contact your system administrator."
  },
  ls_state_persistence_failed: {
    errorCode: 500,
    errorMessage: "We were unable to securely start your login process. Please refresh the page and try again.",
  },
  ls_state_retrieval_failed: {
    errorCode: 500,
    errorMessage:
      "We are experiencing a temporary technical issue verifying your login. Please wait a moment and try again. If the problem persists, contact OSTTRA Support.",
  },
  ls_session_persistence_failed: {
    errorCode: 500,
    errorMessage: "We verified your identity but couldn't finalize your session. Please try again.",
  },
  ls_user_details_lookup_failed: {
    errorCode: 500,
    errorMessage:
      "We are having trouble connecting to our account discovery service. Please wait a moment and try again.",
  },
  ls_token_lookup_failed: {
    errorCode: 500,
    errorMessage: "We are having trouble issuing an access token. Please wait a moment and try again.",
  },
  ls_internal_server_error: {
    errorCode: 500,
    errorMessage:
      "We encountered an unexpected problem while processing your login. Please try again in a few minutes or contact OSTTRA support if the issue continues.",
  },
  ls_idp_token_generation_failed: {
    errorCode: 500,
    errorMessage:
      "We are having trouble preparing your secure login environment. This is a temporary technical issue. Please refresh the page or try again in a few minutes.",
  },
  ls_bad_request_params: {
    errorCode: 400,
    errorMessage:
      "We couldn't process your login because some required information is missing or incorrect. Please return to the application and try again.",
  },
  ls_user_application_lookup_failed: {
    errorCode: 500,
    errorMessage: "Something went wrong. Please log out and try logging in again.",
  },
  ls_invalid_domain: {
    errorCode: 400,
    errorMessage: "Invalid email domain. Please verify your email or contact your administrator.",
  },
  ls_domain_unregistered: {
    errorCode: 404,
    errorMessage: "Organization domain not registered. Please verify your email or contact your administrator.",
  },
  ls_domain_disabled: {
    errorCode: 400,
    errorMessage: "Your organization domain has been disabled. Please contact your system administrator.",
  },
  ls_domain_lookup_failed: {
    errorCode: 400,
    errorMessage: "Email not recognized. Please check the spelling of your company email.",
  },
  ls_otp_generation_failed: {
    errorCode: 500,
    errorMessage: "We couldn't send the code to your inbox. Please try again after few minutes.",
  },
  ls_user_blocked: {
    errorCode: 429,
    errorMessage: "Too many attempts. Your account is temporarily locked.",
  },
  ls_invalid_email: {
    errorCode: 400,
    errorMessage: "Invalid email format. Please check for typos and enter your full email address.",
  },
  ls_session_retrieval_failed: lsSessionSvcError,
  ls_saml_session_retrieval_failed: lsSessionSvcError,
  as_invalid_scope: {
    errorCode: 400,
    errorMessage: "The requested scope is not valid.",
  },
  as_identity_sync_error: {
    errorCode: 500,
    errorMessage:
      "An internal error occurred while synchronizing your login profile. Please try signing in again. If this issue persists, please contact the OSTTRA Support team.",
  },
  as_inactive_client: {
    errorCode: 403,
    errorMessage: "This application is currently disabled for maintenance or security reasons.",
  },
  as_persistence_error: {
    errorCode: 503,
    errorMessage:
      "We are temporarily unable to verify your login session. This is likely a connection issue; please try again in a few minutes.",
  },
  as_unauthorized_user: {
    errorCode: 403,
    errorMessage:
      "You do not have permission to access this application. Please request access from your administrator.",
  },
  as_authorization_error: {
    errorCode: 401,
    errorMessage: "Application authentication failed. Please verify your credentials.",
  },
  as_internal_server_error: {
    errorCode: 500,
    errorMessage: "An unexpected error occurred. If the problem persists, please reach out to our support team.",
  },
  as_malformed_auth_request: {
    errorCode: 400,
    errorMessage:
      "We are unable to process your sign-in request because some required information is missing. Please restart the login process from your application.",
  },
  as_expired_client_secret: {
    errorCode: 400,
    errorMessage: "The security credentials for this application have expired.",
  },
  as_missing_client_secret: {
    errorCode: 400,
    errorMessage: "No security credentials found for this application.",
  },
  as_access_denied: {
    errorCode: 403,
    errorMessage:
      "Access Denied. The request could not be authorized. Please ensure you are starting the sign-in process from an approved application.",
  },
  as_authorization_invalid_secret: {
    errorCode: 401,
    errorMessage: "The security credentials provided are not valid. Access is denied.",
  },
  as_invalid_logout_request: {
    errorCode: 400,
    errorMessage:
      "We encountered a problem processing your sign-out request due to invalid security parameters. Please return to the application and try again.",
  },
  as_logout_success: {
    errorCode: 400,
    errorMessage:
      "You have been safely signed out of your account. We couldn't automatically return you to the application due to an invalid request parameter.",
  },
  as_logout_error: {
    errorCode: 500,
    errorMessage:
      "We encountered a problem processing your sign-out request due to a technical issue. Please return to the application and try again.",
  },
  as_session_data_unavailable: asSessionDataMissingError,
  as_browser_session_missing: asSessionDataMissingError,
  as_authorization_invalid_client: asInvalidClientError,
  as_invalid_client: asInvalidClientError,
  invalid_client: asInvalidClientError,
  invalid_request: {
    errorCode: 400,
    errorMessage:
      "We couldn't sign you in because the application sent an invalid authorization request. Please return to the application and try again.",
  },
  unsupported_response_type: {
    errorCode: 400,
    errorMessage: "The provided response type is invalid.",
  },
  invalid_grant: {
    errorCode: 401,
    errorMessage: "Your login session has expired or is no longer valid. Please restart the login process to continue.",
  },
};

export const getErrorDetails = (code: string | null): ErrorDetails => {
  if (!code) {
    return ERROR_MAP["sp_internal_server_error"];
  }
  return ERROR_MAP[code] ?? ERROR_MAP["sp_internal_server_error"];
};
